import { Column, FlatColumnConfig, FlatColumnType, PreviewColumn, SortType } from "@coeff/api";
import React, { useContext, MutableRefObject } from "react";

// PPress parameter types
export type PPressParamsBase = {
  data_import_name?: string;
  disable_columns_override?: boolean;
  selected_preview_columns?: Column[];
  launch_preview_modal?: boolean;
  flattened_columns?: Array<FlatColumnConfig>;
  default_flattened_column_type?: FlatColumnType;
  static_recipe_key?: string;
};

export type PPressParams = PPressParamsBase & {
  [param: string]: any;
};

export type PPressFormState = {
  is_loading: boolean;
  form_jsx?: string;
  side_pane_form_jsx?: string;
  toolbar_form_jsx?: string;
  no_form_jsx?: boolean;
  refresh_preview_data?: boolean;
  refresh_preview_data_prompt?: boolean;
  show_render_and_refresh_prompt?: boolean;
  display_refresh_button?: boolean;
  fully_valid?: boolean;
  hide_security_footer?: boolean;
  reset_filter_clause?: any;
};

export type PPressFormStateWithPPressParams<P extends PPressParams = PPressParams> =
  PPressFormState & {
    ppress_params: P;
  };

export type ListenersType<T extends Array<any> = any[]> = MutableRefObject<{
  [listenerId: string]: (...arg: T) => void;
}>;

const useListenAction = <T extends Array<any> = any[]>(
  listeners?: ListenersType<T>
): [ListenersType<T>, (...arg: T) => void] => {
  // Stub implementation - actual implementation is in addon
  const listenersRef = React.useRef<{ [key: string]: (...arg: T) => void }>({});
  const act = (...arg: T) => {
    Object.values(listenersRef.current).forEach(listener => listener(...arg));
  };
  return [listenersRef as ListenersType<T>, act];
};

export enum OnChangeTrigger {
  // Clears stale preview data(eg. columns, rows) so it's not shown in the UI
  CLEAR_PREVIEW_DATA = "clear_preview_data",
  // Triggers a call to fetch preview data
  REFRESH_PREVIEW_DATA = "refresh_preview_data",
  // Shows the prompt to refresh preview data
  REFRESH_PREVIEW_DATA_PROMPT = "refresh_preview_data_prompt",
  // Show loading state while waiting for preview data to refresh
  // NOTE: This doesn't trigger the preview data call
  EXPECT_PREVIEW_DATA = "expect_preview_data",
  SHOW_RENDER_AND_REFRESH_PROMPT = "show_render_and_refresh_prompt",
}

export const useFilterSortListenAction = () => {
  const [listenAddFilter, signalAddFilter] = useListenAction<[string]>();
  const [listenAddSort, signalAddSort] = useListenAction<[string, SortType]>();
  return { listenAddFilter, signalAddFilter, listenAddSort, signalAddSort };
};

export type PPressContextParams<P extends PPressParams = PPressParams> =
  PPressFormStateWithPPressParams<P> & {
    oauthInProgress?: boolean;
    setOauthInProgress?: (value: boolean) => void;
    updatePPressParams: (ppressParams: Partial<P>) => void;
    availableColumnsFlat?: Array<PreviewColumn>;
    onFlattenedColumnChange?: (
      ppressParams: PPressParams,
      values: FlatColumnConfig
    ) => Promise<PPressParams>;
    updateFormFromServer: (ppressParams?: Partial<P>) => Promise<any>;
    triggersOnChange: (triggers: Array<OnChangeTrigger>) => void;
    signalAddFilter: (columnName: string) => void;
    listenAddFilter: ListenersType<[string]>;
    signalAddSort: (columnName: string, sortType: SortType) => void;
    listenAddSort: ListenersType<[string, SortType]>;
    currentPreviewModalTrigger?: string | undefined;
    displayTemplatesPopover: boolean;
    onStartWithTemplate: () => void;
  };

export const PpressContext = React.createContext<PPressContextParams | null>(null);

export const usePPressContext = <T extends PPressParams>() =>
  useContext(PpressContext) as unknown as PPressContextParams<T>;

export interface PPressComponentProps {
  updateFromServerOnChange?: boolean;
  triggersOnChange?: Array<OnChangeTrigger>;
}
